import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { isValidISOCode } from "../../helper";
import {
  INDIAN_STATES,
  DIAL_CODES,
  COUNTRIES,
  splitContact,
  PHONE_CODE_OPTIONS,
  isIndia,
  joinContact
} from "../../geo";
import api from "../../api";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

import SnackBarComponent from "../../components/SnackBarComponent";
registerPlugin(FilePondPluginImagePreview);

export default function MasterForm() {
  const { dealer_code, country_code } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(dealer_code && country_code);

  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState([]);
  const [mainImageFile, setMainImageFile] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [mainImagePreviewUrl, setMainImagePreviewUrl] = useState("");
  const [coordinateHelpOpen, setCoordinateHelpOpen] = useState(false);


  const [formData, setFormData] = useState({
    country_code: "",
    dealer_code: "",
    name: "",
    type: "",
    order: 0,

    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    landmark: "",
    latitude: "",
    longitude: "",
    directions_link: "",

    // UI-only: recombined into `contact` on submit, never sent on its own
    phone_code: "",
    contact: "",
    legal_data: "",
    email: "",
    website: "",
    website_location: "",

    logo: "",
    image_link: "",

    operational_tag: "",
    operating_days: "",
    operating_hours: "",

    // State Mappings (Sent to Lambda in the main payload)
    mapped_states: [],

    operational: false,
    visible: false,
    test_ride_available: false,
    home_test_ride_available: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });


  // 1. Fetch Data (Edit Mode)
  useEffect(() => {
    if (isEditing) {
      const fetchData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("accessToken");
        try {
          // A. Fetch Dealer Data
          const response = await api.get("/api/dealer/all", {
            headers: { Authorization: `Bearer ${token}` },
          });

          const item = response.data.data.find(
            (r) => r.dealer_code === dealer_code && r.country_code === country_code
          );

          // B. Fetch Mappings
          let existingMappings = [];
          try {
            const mappingRes = await api.get("/api/dealer/all", {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                fetch_mappings: 'true',
                dealer_code: dealer_code
              }
            });
            existingMappings = mappingRes.data.states || [];
          } catch (mapErr) {
            console.warn("No mappings found", mapErr);
          }

          // C. Fetch Indemnity — legal_data lives in dealer-indemnity, not on
          // the dealer row, so it has to be asked for separately
          let existingLegalData = "";
          try {
            const indemnityRes = await api.get("/api/dealer/all", {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                fetch_indemnity: 'true',
                dealer_code: dealer_code
              }
            });
            existingLegalData = indemnityRes.data.legal_data || "";
          } catch (indErr) {
            console.warn("No indemnity found", indErr);
          }

          if (item) {
            const { dial, number } = splitContact(item.contact);
            const flatData = {
              ...item,
              operating_days: item.operating?.days || "",
              operating_hours: item.operating?.hours || "",
              mapped_states: existingMappings,
              legal_data: existingLegalData,
              // Fall back to the dealer's own country when the stored number
              // carries no dial code yet
              phone_code: dial || (DIAL_CODES[item.country_code] ? `+${DIAL_CODES[item.country_code]}` : ""),
              contact: number
            };
            setFormData((prev) => ({ ...prev, ...flatData }));
            setOriginalData(flatData);
            setLogoPreviewUrl(flatData.logo || "");
            setMainImagePreviewUrl(flatData.image_link || "");
          }
        } catch (error) {
          console.error("Error fetching data", error);
          setSnackbar({ open: true, message: "Failed to load data", severity: "error" });
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [dealer_code, country_code, isEditing]);

  const handleFilePondLoad = (source, load, error, progress, abort, headers) => {
    fetch(source).then(res => res.blob()).then(load).catch(error);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Force Uppercase for Country Code
    if (name === "country_code") {
      finalValue = value.toUpperCase();
    }
    // Auto-Capitalize First Letter
    else if (["state", "country", "city"].includes(name)) {
      finalValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    }

    setFormData({ ...formData, [name]: finalValue });

    if (name === "logo" || name === "image_link") {
      const trimmedValue = finalValue.trim();
      const setPreviewUrl = name === "logo" ? setLogoPreviewUrl : setMainImagePreviewUrl;

      if (!trimmedValue) {
        setPreviewUrl("");
      } else if (isValidUrl(trimmedValue)) {
        setPreviewUrl(trimmedValue);
      }
    }
  };

  // Country drives country_code and the phone code, so they can never disagree.
  // country_code is half the primary key, which is why the field is locked while
  // editing.
  const handleCountryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      country: selected ? selected.name : "",
      country_code: selected ? selected.code : prev.country_code,
      phone_code: selected ? selected.dial : prev.phone_code,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const isValidUrl = (string) => {
    if (!string) return true;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    // --- Validation ---
    if (formData.country_code.length !== 2) {
      setSnackbar({ open: true, message: "Country Code must be exactly 2 characters (e.g., IN, US).", severity: "warning" });
      setIsSaving(false);
      return;
    }

    if (!isValidISOCode(formData.country_code)) {
      setSnackbar({
        open: true,
        message: "Invalid Country Code. Please enter a valid 2-letter ISO code (e.g., IN, US).",
        severity: "warning"
      });
      return;
    }

    const coordExtractorRegex = /(\d+)\s*°\s*(\d+\.?\d*)\s*'\s*(\d+\.?\d*)?\s*"?\s*([NSEW])/i;
    if (formData.latitude && !coordExtractorRegex.test(formData.latitude)) {
      setSnackbar({ open: true, message: "Invalid Latitude Format.", severity: "warning" });
      setIsSaving(false);
      return;
    }
    if (formData.longitude && !coordExtractorRegex.test(formData.longitude)) {
      setSnackbar({ open: true, message: "Invalid Longitude Format.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    if (formData.operational_tag && formData.operational_tag.length > 150) {
      setSnackbar({ open: true, message: "Operational Tag cannot exceed 150 characters.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      setSnackbar({ open: true, message: "Invalid Email Format.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    if (formData.directions_link && !isValidUrl(formData.directions_link)) {
      setSnackbar({ open: true, message: "Invalid Directions Link.", severity: "warning" });
      setIsSaving(false);
      return;
    }
    if (formData.website && !isValidUrl(formData.website)) {
      setSnackbar({ open: true, message: "Invalid Website Link.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    if (isNaN(parseInt(formData.order))) {
      setSnackbar({ open: true, message: "Order Priority must be a number.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    const p = new FormData();
    Object.keys(formData).forEach(key => {
      // phone_code is a UI-only split of contact, recombined below
      if (key === 'phone_code') return;
      else if (key === 'contact') p.append(key, joinContact(formData.phone_code, formData.contact));
      else if (['mapped_states'].includes(key)) p.append(key, JSON.stringify(formData[key]));
      else if (['operational', 'visible', 'test_ride_available', 'home_test_ride_available'].includes(key)) p.append(key, formData[key].toString());
      else p.append(key, formData[key] === null ? "" : formData[key]);
    });

    if (logoFile.length > 0 && logoFile[0].file) {
      p.append("logoFile", logoFile[0].file);
      if (originalData.logo) p.append("old_logo", originalData.logo);
      p.set("logo", "");
    }

    if (mainImageFile.length > 0 && mainImageFile[0].file) {
      p.append("imageFile", mainImageFile[0].file);
      if (originalData.image_link) p.append("old_image_link", originalData.image_link);
      p.set("image_link", "");
    }

    const token = localStorage.getItem("accessToken");

    try {
      if (isEditing) {
        await api.put("/api/dealer/update", p, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.post("/api/dealer/create", p, { headers: { Authorization: `Bearer ${token}` } });
      }

      setSnackbar({
        open: true,
        message: isEditing ? "Updated Successfully!" : "Created Successfully!",
        severity: "success"
      });

      setTimeout(() => navigate("/dealer/dealership-master"), 1000);
    } catch (error) {
      console.error("Error saving data", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.response?.data?.error || "Failed to save",
        severity: "error"
      });
      setIsSaving(false);
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>
      {snackbar.open && (
        <SnackBarComponent
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      )}

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/dealer/dealership-master")} sx={{ mb: 2 }}>
        Back to List
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditing ? "Edit Dealership" : "Add New Dealership"}
        </Typography>

        <form onSubmit={handleSubmit}>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>IDENTITY</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Dealer Code" name="dealer_code" value={formData.dealer_code} onChange={handleInputChange} disabled={isEditing} required fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Country Code (2 Char)"
                name="country_code"
                value={formData.country_code}
                disabled
                required
                fullWidth
                helperText="Set automatically from the Country you pick below."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Name" name="name" value={formData.name} onChange={handleInputChange} required fullWidth />
            </Grid>

            {/* TYPE DROPDOWN */}
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select name="type" value={formData.type} label="Type" onChange={handleInputChange}>
                  <MenuItem value="UV SPACE STATION">UV SPACE STATION</MenuItem>
                  <MenuItem value="UV SPACE POD">UV SPACE POD</MenuItem>
                  <MenuItem value="COCO">COCO</MenuItem>
                  {!["UV SPACE STATION", "UV SPACE POD", "COCO"].includes(formData.type) && formData.type && (<MenuItem value={formData.type}>{formData.type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}><TextField label="Operational Tag" name="operational_tag" value={formData.operational_tag} onChange={handleInputChange} fullWidth inputProps={{ maxLength: 150 }} helperText={`${(formData.operational_tag || '').length}/150`} /></Grid>
            <Grid item xs={6}>
              <TextField
                label="Order Priority"
                name="order"
                type="number"
                value={formData.order}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* LOCATION */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>LOCATION</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField label="Address" name="address" value={formData.address} onChange={handleInputChange} multiline rows={2} fullWidth required /></Grid>
            <Grid item xs={4}><TextField label="City" name="city" value={formData.city} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={4}>
              {isIndia(formData) ? (
                <FormControl fullWidth required>
                  <InputLabel id="state-label">State</InputLabel>
                  {/* Not handleInputChange — its title-casing would mangle names like
                      "Andaman and Nicobar Islands" and break the match with the list */}
                  <Select
                    labelId="state-label"
                    name="state"
                    value={formData.state}
                    label="State"
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  >
                    {INDIAN_STATES.map((state) => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                    {/* Keep an existing value that predates this list selectable */}
                    {formData.state && !INDIAN_STATES.includes(formData.state) && (
                      <MenuItem value={formData.state}>{formData.state}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              ) : (
                /* No curated subdivision list outside India, so this stays
                   optional free text */
                <TextField label="State (optional)" name="state" value={formData.state} onChange={handleInputChange} fullWidth />
              )}
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                value={COUNTRIES.find((c) => c.name === formData.country) || null}
                onChange={(event, selected) => handleCountryChange(selected)}
                disabled={isEditing}
                renderInput={(params) => (
                  <TextField {...params} label="Country" required fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={4}><TextField label="Pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={8}><TextField label="Landmark" name="landmark" value={formData.landmark} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={6}>
              <TextField
                label="Latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                fullWidth
                placeholder={"12° 58' 20.4\" N"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Coordinate format">
                        <IconButton
                          aria-label="Show latitude and longitude format"
                          edge="end"
                          onClick={() => setCoordinateHelpOpen(true)}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                fullWidth
                placeholder={"77° 35' 30.1\" E"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Coordinate format">
                        <IconButton
                          aria-label="Show latitude and longitude format"
                          edge="end"
                          onClick={() => setCoordinateHelpOpen(true)}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}><TextField label="Directions Link" name="directions_link" value={formData.directions_link} onChange={handleInputChange} fullWidth placeholder="https://maps.google.com/..." /></Grid>
          </Grid>

          {/* STATE MAPPINGS — India only, the mapping table is India-scoped */}
          {isIndia(formData) && (
            <>
              <Divider sx={{ my: 4 }} />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>STATE MAPPINGS</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="state-mapping-label">Mapped States</InputLabel>
                    <Select
                      labelId="state-mapping-label"
                      multiple
                      name="mapped_states"
                      value={formData.mapped_states || []}
                      onChange={(e) => setFormData({ ...formData, mapped_states: e.target.value })}
                      input={<OutlinedInput label="Mapped States" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {INDIAN_STATES.map((state) => (
                        <MenuItem key={state} value={state}>
                          <Checkbox checked={(formData.mapped_states || []).indexOf(state) > -1} />
                          <ListItemText primary={state} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 4 }} />

          {/* CONTACT */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>CONTACT & MEDIA</Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              {/* The selected country's code first, then the rest as a fallback.
                  Typeable: filters on either the code or the country name. */}
              <Autocomplete
                options={PHONE_CODE_OPTIONS(formData)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.dial === value.dial}
                value={PHONE_CODE_OPTIONS(formData).find((o) => o.dial === formData.phone_code) || null}
                onChange={(event, selected) =>
                  setFormData((prev) => ({ ...prev, phone_code: selected ? selected.dial : "" }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Code" required fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Contact Number"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={5}><TextField label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField label="Website URL" name="website" value={formData.website} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Website Location Name" name="website_location" value={formData.website_location} onChange={handleInputChange} fullWidth /></Grid>

            {/* Indemnity is India-only; the backend enforces it, this just hides the field */}
            {isIndia(formData) && (
              <Grid item xs={12}>
                <TextField
                  label="Legal Data (Indemnity)"
                  name="legal_data"
                  value={formData.legal_data}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  helperText='Leave blank to use the default indemnity.'
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Logo</Typography>
              <TextField
                label="Logo URL"
                name="logo"
                value={formData.logo}
                onChange={handleInputChange}
                fullWidth
                disabled={logoFile.length > 0}
                helperText="Enter URL OR upload a file. When one is used, the other is disabled."
                sx={{ mb: 2 }}
              />
              <FilePond
                files={logoFile}
                onupdatefiles={setLogoFile}
                allowMultiple={false}
                acceptedFileTypes={['image/*']}
                labelIdle='Drag & Drop your logo or <span class="filepond--label-action">Browse</span>'
                disabled={formData.logo.trim().length > 0}
              />
              {logoPreviewUrl && logoFile.length === 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Logo Preview</Typography>
                  <img
                    src={logoPreviewUrl}
                    alt="Logo Preview"
                    style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Main Image</Typography>
              <TextField
                label="Image Link URL"
                name="image_link"
                value={formData.image_link}
                onChange={handleInputChange}
                fullWidth
                disabled={mainImageFile.length > 0}
                helperText="Enter URL OR upload a file. When one is used, the other is disabled."
                sx={{ mb: 2 }}
              />
              <FilePond
                files={mainImageFile}
                onupdatefiles={setMainImageFile}
                allowMultiple={false}
                acceptedFileTypes={['image/*']}
                labelIdle='Drag & Drop your main image or <span class="filepond--label-action">Browse</span>'
                disabled={formData.image_link.trim().length > 0}
              />
              {mainImagePreviewUrl && mainImageFile.length === 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Image Preview</Typography>
                  <img
                    src={mainImagePreviewUrl}
                    alt="Main preview"
                    style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'cover', borderRadius: '4px' }}
                  />
                </Box>
              )}
            </Grid>

          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* OPERATING SCHEDULE */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>OPERATING SCHEDULE</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField label="Operating Days" name="operating_days" value={formData.operating_days} onChange={handleInputChange} placeholder="e.g., Mon - Sun" fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Operating Hours" name="operating_hours" value={formData.operating_hours} onChange={handleInputChange} placeholder="e.g., 10:00 - 20:30" fullWidth /></Grid>
          </Grid>

          {/* SETTINGS */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <FormControlLabel control={<Switch checked={Boolean(formData.operational)} onChange={handleSwitchChange} name="operational" />} label="Operational (Active)" />
            <FormControlLabel control={<Switch checked={Boolean(formData.visible)} onChange={handleSwitchChange} name="visible" />} label="Visible on Website" />
            <FormControlLabel control={<Switch checked={Boolean(formData.test_ride_available)} onChange={handleSwitchChange} name="test_ride_available" />} label="Test Ride Available" />
            <FormControlLabel control={<Switch checked={Boolean(formData.home_test_ride_available)} onChange={handleSwitchChange} name="home_test_ride_available" />} label="Home Test Ride Available" />
          </Box>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate("/dealer/dealership-master")}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Dealership"}
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog open={coordinateHelpOpen} onClose={() => setCoordinateHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Expected Coordinate Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter latitude and longitude in degrees, minutes, and seconds (With double quotes) with the direction at the end.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Format
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", mb: 2 }}>
            degrees° minutes' seconds" direction
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Examples
          </Typography>
          <Typography variant="body2">Latitude: 12° 58' 20.4" N</Typography>
          <Typography variant="body2">Longitude: 77° 35' 30.1" E</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Use N or S for latitude, and E or W for longitude.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoordinateHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
